import { Head, useForm } from "@inertiajs/react";
import { Button, Box, Flex, Heading, Text, TextField, Container, Section } from "@radix-ui/themes";
import { Sparkles, BarChart3, Zap, Lock } from "lucide-react";

function LandingShow() {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    email: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/waitlist");
  };

  return (
    <>
      <Head title="Formbot - Turn Survey Responses into Insights in Seconds" />

      {/* Header */}
      <Box style={{
        borderBottom: "1px solid var(--gray-a5)",
        backgroundColor: "var(--color-background)",
        position: "sticky",
        top: 0,
        zIndex: 50
      }}>
        <Container size="4">
          <Flex justify="between" align="center" py="4">
            <Heading size="6" style={{ color: "var(--accent-9)" }}>
              Formbot
            </Heading>
            <Button asChild variant="soft" size="2">
              <a href="/auth/google_oauth2">
                Login
              </a>
            </Button>
          </Flex>
        </Container>
      </Box>

      {/* Hero Section */}
      <Section size="3">
        <Container size="3">
          <Flex direction="column" align="center" gap="6" style={{ textAlign: "center", paddingTop: "var(--space-8)" }}>
            <Box style={{ width: "100%" }}>
              <Heading size="9" mb="4" style={{
                maxWidth: "800px",
                margin: "0 auto",
                lineHeight: 1.1,
                background: "linear-gradient(135deg, var(--accent-11) 0%, var(--accent-9) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}>
                Turn messy survey responses into organized insights
              </Heading>
              <Text size="5" color="gray" style={{ maxWidth: "600px", margin: "0 auto" }}>
                Stop manually reading through hundreds of responses. Let AI automatically categorize your feedback, identify patterns, and show you what matters most.
              </Text>
            </Box>

            {/* Waitlist Form */}
            <Box style={{ width: "100%", maxWidth: "500px" }}>
              <form onSubmit={handleSubmit}>
                <Flex direction="column" gap="3">
                  <TextField.Root
                    size="3"
                    placeholder="Your name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    required
                  />
                  <TextField.Root
                    size="3"
                    type="email"
                    placeholder="Your email"
                    value={data.email}
                    onChange={(e) => setData("email", e.target.value)}
                    required
                  />
                  <Button
                    size="4"
                    variant="classic"
                    type="submit"
                    disabled={processing}
                    style={{ cursor: processing ? "not-allowed" : "pointer" }}
                  >
                    {processing ? "Joining..." : "Join the Waitlist"}
                  </Button>
                  {errors.email && (
                    <Text size="2" color="red">{errors.email}</Text>
                  )}
                </Flex>
              </form>
              <Text size="2" color="gray" mt="3">
                Get early access when we launch. No spam, ever.
              </Text>
            </Box>

            {/* Screenshot */}
            <Box mt="6" style={{
              width: "100%",
              maxWidth: "1000px",
              borderRadius: "var(--radius-4)",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "1px solid var(--gray-a5)"
            }}>
              <picture>
                <source srcSet="/images/screenshot-optimized.webp" type="image/webp" />
                <img
                  src="/images/screenshot-optimized.jpg"
                  alt="Formbot Analysis Results showing categorized survey responses"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </picture>
            </Box>
          </Flex>
        </Container>
      </Section>

      {/* Features Section */}
      <Section size="3" style={{ backgroundColor: "var(--gray-a2)" }}>
        <Container size="3">
          <Heading size="8" align="center" mb="8">
            Why teams love Formbot
          </Heading>

          <Flex gap="6" direction={{ initial: "column", md: "row" }} wrap="wrap" justify="center">
            {/* Feature 1 */}
            <Box style={{ flex: "1 1 280px", maxWidth: "350px" }}>
              <Flex direction="column" gap="3">
                <Box style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "var(--radius-3)",
                  backgroundColor: "var(--accent-3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Sparkles size={24} style={{ color: "var(--accent-9)" }} />
                </Box>
                <Heading size="5">AI-Powered Categorization</Heading>
                <Text color="gray">
                  Claude AI reads every response and intelligently groups similar feedback together. No manual tagging required.
                </Text>
              </Flex>
            </Box>

            {/* Feature 2 */}
            <Box style={{ flex: "1 1 280px", maxWidth: "350px" }}>
              <Flex direction="column" gap="3">
                <Box style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "var(--radius-3)",
                  backgroundColor: "var(--accent-3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <BarChart3 size={24} style={{ color: "var(--accent-9)" }} />
                </Box>
                <Heading size="5">Visual Insights</Heading>
                <Text color="gray">
                  See heat maps, percentages, and counts at a glance. Understand what your users are really saying in seconds.
                </Text>
              </Flex>
            </Box>

            {/* Feature 3 */}
            <Box style={{ flex: "1 1 280px", maxWidth: "350px" }}>
              <Flex direction="column" gap="3">
                <Box style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "var(--radius-3)",
                  backgroundColor: "var(--accent-3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Zap size={24} style={{ color: "var(--accent-9)" }} />
                </Box>
                <Heading size="5">Works with Google Sheets</Heading>
                <Text color="gray">
                  No data migration needed. Connect your Google account and analyze any sheet in seconds. Your data stays in Google Drive.
                </Text>
              </Flex>
            </Box>

            {/* Feature 4 */}
            <Box style={{ flex: "1 1 280px", maxWidth: "350px" }}>
              <Flex direction="column" gap="3">
                <Box style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "var(--radius-3)",
                  backgroundColor: "var(--accent-3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Lock size={24} style={{ color: "var(--accent-9)" }} />
                </Box>
                <Heading size="5">Secure & Private</Heading>
                <Text color="gray">
                  Your data is never stored permanently. We only read what's needed for analysis, and results are stored in your account.
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Container>
      </Section>

      {/* Use Cases Section */}
      <Section size="3">
        <Container size="3">
          <Heading size="8" align="center" mb="8">
            Perfect for
          </Heading>

          <Flex gap="5" direction="column" style={{ maxWidth: "800px", margin: "0 auto" }}>
            <Box p="5" style={{
              backgroundColor: "var(--gray-a2)",
              borderRadius: "var(--radius-3)",
              border: "1px solid var(--gray-a5)"
            }}>
              <Heading size="4" mb="2">Product Managers</Heading>
              <Text color="gray">
                Analyze feature requests, NPS comments, and user feedback surveys. Identify the most requested features and pain points instantly.
              </Text>
            </Box>

            <Box p="5" style={{
              backgroundColor: "var(--gray-a2)",
              borderRadius: "var(--radius-3)",
              border: "1px solid var(--gray-a5)"
            }}>
              <Heading size="4" mb="2">Customer Success Teams</Heading>
              <Text color="gray">
                Categorize support tickets and customer feedback. Spot trends before they become problems and improve response times.
              </Text>
            </Box>

            <Box p="5" style={{
              backgroundColor: "var(--gray-a2)",
              borderRadius: "var(--radius-3)",
              border: "1px solid var(--gray-a5)"
            }}>
              <Heading size="4" mb="2">Researchers</Heading>
              <Text color="gray">
                Analyze qualitative survey data at scale. Turn thousands of open-ended responses into actionable themes and insights.
              </Text>
            </Box>

            <Box p="5" style={{
              backgroundColor: "var(--gray-a2)",
              borderRadius: "var(--radius-3)",
              border: "1px solid var(--gray-a5)"
            }}>
              <Heading size="4" mb="2">Marketing Teams</Heading>
              <Text color="gray">
                Understand customer sentiment from reviews, testimonials, and campaign feedback. Find the words your customers actually use.
              </Text>
            </Box>
          </Flex>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section size="3" style={{ backgroundColor: "var(--accent-2)" }}>
        <Container size="2">
          <Flex direction="column" align="center" gap="5" style={{ textAlign: "center" }}>
            <Heading size="8">
              Ready to understand your users better?
            </Heading>
            <Text size="4" color="gray">
              Join the waitlist and be the first to know when we launch.
            </Text>
            <Button asChild size="4" variant="classic">
              <a href="#" onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}>
                Join the Waitlist
              </a>
            </Button>
          </Flex>
        </Container>
      </Section>

      {/* Footer */}
      <Box style={{
        borderTop: "1px solid var(--gray-a5)",
        backgroundColor: "var(--gray-a2)",
        padding: "var(--space-6) 0"
      }}>
        <Container size="4">
          <Flex justify="between" align="center">
            <Text size="2" color="gray">
              © 2025 Formbot
            </Text>
            <Text size="2" color="gray">
              Created by{" "}
              <a
                href="https://x.com/_vojto"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "var(--accent-11)",
                  textDecoration: "none"
                }}
                className="hover:opacity-75"
              >
                Vojtech Rinik
              </a>
            </Text>
          </Flex>
        </Container>
      </Box>
    </>
  );
}

// Disable the default layout for the landing page
LandingShow.layout = (page: React.ReactNode) => page;

export default LandingShow;
